import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  getAuditTrails,
  getAuditActions,
  AuditLogEntry,
  AuditTrailsParams,
} from '../../services/auditTrailService';
import { format, parseISO } from 'date-fns';
import { useUI } from '../../context/UIContext';
import {
  RefreshIcon,
  SearchIcon,
  ChevronDownIcon,
  ArrowRightIcon,
} from '../../components/common/Icons';
import '../../styles/AuditTrailsPage.css';

const ACTOR_TYPE_LABELS: Record<string, string> = {
  client: 'Client',
  stylist: 'Stylist',
  admin: 'Admin',
  system: 'System',
};

const CATEGORY_LABELS: Record<string, string> = {
  auth: 'Login / Auth',
  appointment: 'Appointment',
  payment: 'Payment',
  profile: 'Profile',
  subscription: 'Subscription',
  support: 'Support',
  system: 'System',
};

const RATE_LIMIT_BACKOFF_MS = 10000; // don't refetch for 10s after 429
const ERROR_BACKOFF_MS = 3000;      // don't refetch for 3s after any error (stops 404/5xx loops)

// Deep links (for example from Account Deletions): ?userId=<id>&action=<action>.
const OBJECT_ID = /^[0-9a-f]{24}$/i;
const ACTION_CODE = /^[a-z0-9_]{1,80}$/;

const AuditTrailsPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const linkedUserId = OBJECT_ID.test(searchParams.get('userId') || '') ? searchParams.get('userId') || '' : '';
  const linkedAction = ACTION_CODE.test(searchParams.get('action') || '') ? searchParams.get('action') || '' : '';
  const { showSnackbar } = useUI();
  const showSnackbarRef = useRef(showSnackbar);
  showSnackbarRef.current = showSnackbar;

  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [categories, setCategories] = useState<string[]>([]);
  const [actions, setActions] = useState<string[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const [filters, setFilters] = useState<AuditTrailsParams>({
    page: 1,
    limit: 20,
    actorType: '',
    targetUserType: '',
    category: '',
    action: linkedAction,
    userId: linkedUserId,
    email: '',
    dateFrom: '',
    dateTo: '',
  });
  const [appliedFilters, setAppliedFilters] = useState<AuditTrailsParams>({ ...filters });

  const last429Ref = useRef<number>(0);
  const lastErrorRef = useRef<number>(0);

  const fetchActions = useCallback(async () => {
    try {
      const res = await getAuditActions();
      setCategories(Array.isArray(res.data?.categories) ? res.data.categories : []);
      setActions(Array.isArray(res.data?.actions) ? res.data.actions : []);
    } catch (err) {
      console.error('Error fetching audit actions:', err);
      setCategories([]);
      setActions([]);
    }
  }, []);

  const fetchLogs = useCallback(async (params: AuditTrailsParams, isRefresh = false) => {
    const now = Date.now();
    if (now - last429Ref.current < RATE_LIMIT_BACKOFF_MS) return;
    if (!isRefresh && now - lastErrorRef.current < ERROR_BACKOFF_MS) return;
    try {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);
      const query: AuditTrailsParams = {
        page: params.page ?? 1,
        limit: params.limit ?? 20,
      };
      if (params.actorType) query.actorType = params.actorType;
      if (params.targetUserType) query.targetUserType = params.targetUserType;
      if (params.category) query.category = params.category;
      if (params.action) query.action = params.action;
      if (params.userId) query.userId = params.userId;
      if (params.email?.trim()) query.email = params.email.trim();
      if (params.dateFrom) query.dateFrom = params.dateFrom;
      if (params.dateTo) query.dateTo = params.dateTo;

      const res = await getAuditTrails(query);
      setLogs(res.data);
      setPagination(res.pagination);
    } catch (err: any) {
      lastErrorRef.current = Date.now();
      const status = err?.response?.status;
      if (status === 429) {
        last429Ref.current = lastErrorRef.current;
        showSnackbarRef.current('Too many requests. Please wait a moment before refreshing.', 'error');
      } else {
        showSnackbarRef.current(err?.response?.data?.message || 'Failed to load audit trails', 'error');
      }
      setLogs([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchActions();
  }, [fetchActions]);

  useEffect(() => {
    fetchLogs(appliedFilters);
    // fetchLogs omitted intentionally: stable callback via ref so effect only runs when filters change (stops 429 loop)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters.page, appliedFilters.limit, appliedFilters.actorType, appliedFilters.targetUserType, appliedFilters.category, appliedFilters.action, appliedFilters.userId, appliedFilters.email, appliedFilters.dateFrom, appliedFilters.dateTo]);

  const handleApplyFilters = () => {
    const next = { ...filters, page: 1 };
    setFilters(next);
    setAppliedFilters(next);
  };

  const handlePageChange = (newPage: number) => {
    setAppliedFilters((prev) => ({ ...prev, page: newPage }));
  };

  const renderActor = (entry: AuditLogEntry) => {
    if (entry.actorType === 'system') return <span className="audit-trails__actor-system">System</span>;
    const name = entry.actorName || entry.actorEmail || entry.actorId || '—';
    return (
      <span className="audit-trails__actor">
        <strong>{name}</strong>
        <span className="audit-trails__actor-type">{ACTOR_TYPE_LABELS[entry.actorType] || entry.actorType}</span>
      </span>
    );
  };

  const renderTarget = (entry: AuditLogEntry) => {
    if (!entry.targetUserId && !entry.targetEmail && !entry.targetName) return <span>—</span>;
    const name = entry.targetName || entry.targetEmail || entry.targetUserId || '—';
    return (
      <span className="audit-trails__target">
        {name}
        {entry.targetUserType && (
          <span className="audit-trails__target-type">{ACTOR_TYPE_LABELS[entry.targetUserType] || entry.targetUserType}</span>
        )}
      </span>
    );
  };

  return (
    <div className="audit-trails">
      <header className="audit-trails__header">
        <div>
          <h1 className="audit-trails__title">Audit Trails</h1>
          <p className="audit-trails__subtitle">
            Login, bookings, payments, and appointment workflow for clients and stylists
          </p>
        </div>
        <button
          type="button"
          className="audit-trails__refresh-btn"
          onClick={() => fetchLogs(appliedFilters, true)}
          disabled={refreshing || loading}
        >
          <RefreshIcon />
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      </header>

      <section className="audit-trails__filters">
        <div className="audit-trails__filters-row">
          <select
            value={filters.actorType}
            onChange={(e) => setFilters((f) => ({ ...f, actorType: e.target.value }))}
            className="audit-trails__select"
          >
            <option value="">All actors</option>
            <option value="client">Client</option>
            <option value="stylist">Stylist</option>
            <option value="admin">Admin</option>
            <option value="system">System</option>
          </select>
          <select
            value={filters.targetUserType}
            onChange={(e) => setFilters((f) => ({ ...f, targetUserType: e.target.value }))}
            className="audit-trails__select"
          >
            <option value="">All targets</option>
            <option value="client">Client</option>
            <option value="stylist">Stylist</option>
            <option value="admin">Admin</option>
          </select>
          <select
            value={filters.category}
            onChange={(e) => setFilters((f) => ({ ...f, category: e.target.value }))}
            className="audit-trails__select"
          >
            <option value="">All categories</option>
            {categories.map((c) => (
              <option key={c} value={c}>{CATEGORY_LABELS[c] || c}</option>
            ))}
          </select>
          <select
            value={filters.action}
            onChange={(e) => setFilters((f) => ({ ...f, action: e.target.value }))}
            className="audit-trails__select"
          >
            <option value="">All actions</option>
            {actions.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
        </div>
        {filters.userId && (
          <div className="audit-trails__filters-row">
            <span className="audit-trails__badge audit-trails__badge--category">
              User {filters.userId}
            </span>
            <button
              type="button"
              className="audit-trails__page-btn"
              onClick={() => {
                const next = { ...filters, userId: '', page: 1 };
                setFilters(next);
                setAppliedFilters(next);
              }}
            >
              Clear user filter
            </button>
          </div>
        )}
        <div className="audit-trails__filters-row">
          <input
            type="text"
            placeholder="Search by email (actor or target)"
            value={filters.email || ''}
            onChange={(e) => setFilters((f) => ({ ...f, email: e.target.value }))}
            className="audit-trails__input"
          />
          <input
            type="date"
            value={filters.dateFrom || ''}
            onChange={(e) => setFilters((f) => ({ ...f, dateFrom: e.target.value }))}
            className="audit-trails__input audit-trails__input--date"
          />
          <input
            type="date"
            value={filters.dateTo || ''}
            onChange={(e) => setFilters((f) => ({ ...f, dateTo: e.target.value }))}
            className="audit-trails__input audit-trails__input--date"
          />
          <button type="button" className="audit-trails__apply-btn" onClick={handleApplyFilters}>
            <SearchIcon />
            Apply
          </button>
        </div>
      </section>

      <div className="audit-trails__table-wrap">
        {loading ? (
          <div className="audit-trails__loading">Loading audit trails…</div>
        ) : (
          <>
            <table className="audit-trails__table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Actor</th>
                  <th>Target</th>
                  <th>Category</th>
                  <th>Action</th>
                  <th>Description</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="audit-trails__empty">
                      No audit entries match your filters.
                    </td>
                  </tr>
                ) : (
                  logs.map((entry) => (
                    <React.Fragment key={entry._id}>
                      <tr className="audit-trails__row">
                        <td className="audit-trails__time">
                          {format(parseISO(entry.createdAt), 'MMM d, yyyy HH:mm:ss')}
                        </td>
                        <td>{renderActor(entry)}</td>
                        <td>{renderTarget(entry)}</td>
                        <td>
                          <span className="audit-trails__badge audit-trails__badge--category">
                            {CATEGORY_LABELS[entry.category] || entry.category}
                          </span>
                        </td>
                        <td>
                          <code className="audit-trails__action">{entry.action}</code>
                        </td>
                        <td className="audit-trails__desc">{entry.description}</td>
                        <td>
                          {Object.keys(entry.details || {}).length > 0 && (
                            <button
                              type="button"
                              className="audit-trails__expand-btn"
                              onClick={() => setExpandedId(expandedId === entry._id ? null : entry._id)}
                              aria-expanded={expandedId === entry._id}
                            >
                              {expandedId === entry._id ? <ChevronDownIcon /> : <ArrowRightIcon />}
                            </button>
                          )}
                        </td>
                      </tr>
                      {expandedId === entry._id && entry.details && Object.keys(entry.details).length > 0 && (
                        <tr className="audit-trails__details-row">
                          <td colSpan={7}>
                            <pre className="audit-trails__details">
                              {JSON.stringify(entry.details, null, 2)}
                            </pre>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>

            {pagination.totalPages > 1 && (
              <div className="audit-trails__pagination">
                <button
                  type="button"
                  className="audit-trails__page-btn"
                  disabled={pagination.page <= 1}
                  onClick={() => handlePageChange(pagination.page - 1)}
                >
                  Previous
                </button>
                <span className="audit-trails__page-info">
                  Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
                </span>
                <button
                  type="button"
                  className="audit-trails__page-btn"
                  disabled={pagination.page >= pagination.totalPages}
                  onClick={() => handlePageChange(pagination.page + 1)}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AuditTrailsPage;
